import useGetProjectCustomFields from '@/api/queries/project/useGetProjectCustomFields';
import EditCustomFieldsSidebar from '@/ui/components/customFieldGroup/EditCustomFieldsSidebar';
import {
  Accordion,
  AccordionWithChildCount,
  Badge,
  Button,
  Card,
  ViewFieldset,
} from '@payaca/ambrosia';

import useGetMyAccountUsersIncludeDeactivated from '@/api/queries/me/useGetMyAccountUsersIncludeDeactivated';
import {
  AnyCustomFieldDefinition,
  CustomFieldDefinition,
  CustomFieldHelperType,
  CustomFieldType,
  FieldsetCustomFieldDefinition,
} from '@payaca/custom-fields';
import { CustomFieldReadonlyGroup } from '@payaca/custom-fields-react';
import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import { CustomFieldGroupRole } from '@payaca/types/custom-fields';
import { FC, useReducer } from 'react';
import { Link } from 'react-router-dom';
import useGetCustomerCustomFields from '../../../api/queries/customers/useGetCustomerCustomFields';
import { PermissionGuard } from '../permissionGuard/PermissionGuard';

type SidebarState = {
  activeSidebar: 'editCustomFields' | null;
  fields?: AnyCustomFieldDefinition[];
  value?: Record<string, any>;
  identifier?: CustomFieldDefinition['identifier'];
  label?: CustomFieldDefinition['label'];
};

type CustomFieldWithValue<T extends CustomFieldType = CustomFieldType> = {
  definition: CustomFieldHelperType[T]['definition'];
  value: CustomFieldHelperType[T]['value'] | null;
};

type SidebarAction =
  | {
      type: 'openEditCustomFields';
      payload: CustomFieldWithValue[];
    }
  | {
      type: 'closeSidebar';
    };

const sidebarReducer = (
  state: SidebarState,
  action: SidebarAction
): SidebarState => {
  switch (action.type) {
    case 'openEditCustomFields': {
      const customFields = action.payload;

      if (
        customFields.length === 1 &&
        customFields[0].definition.type === 'fieldset'
      ) {
        const def = customFields[0].definition;
        return {
          activeSidebar: 'editCustomFields',
          fields: [def],
          value: { [def.identifier]: customFields[0].value },
          identifier: def.identifier,
          label: def.label,
        };
      }

      const validCustomFields = customFields.filter(
        (customField) => customField.definition.type !== 'fieldset'
      );

      const fields = validCustomFields.map(
        (customField) => customField.definition
      );

      const value = validCustomFields.reduce(
        (acc, customField) => {
          acc[customField.definition.identifier] = customField.value;

          return acc;
        },
        {} as Record<string, any>
      );

      return {
        activeSidebar: 'editCustomFields',
        fields,
        value,
      };
    }
    case 'closeSidebar':
      return { activeSidebar: null };
    default:
      return state;
  }
};

type BaseCustomFieldGroupProps = {
  type: CustomFieldGroupRole;
};
type CustomerCustomFieldGroupProps = {
  type: 'customer';
  customerId: string;
};
type ProjectCustomFieldGroupProps = {
  type: 'project';
  projectId: string;
};
export type CustomFieldGroupProps = BaseCustomFieldGroupProps &
  (CustomerCustomFieldGroupProps | ProjectCustomFieldGroupProps);
export const CustomFieldGroup: FC<CustomFieldGroupProps> = (props) => {
  const [sidebarState, sidebarDispatch] = useReducer(sidebarReducer, {
    activeSidebar: null,
  });

  // TODO fix ESLint conditional hook error
  const { customFields } =
    props.type === 'project'
      ? useGetProjectCustomFields(props.projectId) // eslint-disable-line react-hooks/rules-of-hooks
      : useGetCustomerCustomFields(props.customerId); // eslint-disable-line react-hooks/rules-of-hooks
  const fieldsetCustomFields = customFields?.filter(
    (x) => x.field.type === 'fieldset'
  );
  const simpleCustomFields = customFields?.filter(
    (x) => x.field.type !== 'fieldset'
  );

  if (!customFields?.length) {
    return (
      <Card>
        <Card.Body>
          <AccordionWithChildCount
            title="Custom Fields"
            count="0"
            chevronPosition="left"
          >
            <div className="mt-2 flex flex-col gap-4">
              <PermissionGuard
                renderIfHasPermissions={[
                  AccountsPermissions.MANAGE_CUSTOM_FIELD_GROUPS,
                ]}
              >
                <p>
                  <Link to={'/companySettings/custom-fields'}>
                    Configure additional fields
                  </Link>
                </p>
              </PermissionGuard>
              <PermissionGuard
                renderIfMissingPermissions={[
                  AccountsPermissions.MANAGE_CUSTOM_FIELD_GROUPS,
                ]}
              >
                <p>Contact admin to configure additional fields</p>
              </PermissionGuard>
            </div>
          </AccordionWithChildCount>
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {fieldsetCustomFields?.map((fieldset) => {
          const fieldsetDefinition = new FieldsetCustomFieldDefinition(
            fieldset.field.schema
          );
          const value = fieldset.value ? JSON.parse(fieldset.value) : null;

          return (
            <CustomFieldsViewCard
              key={fieldsetDefinition.identifier}
              label={fieldsetDefinition.label}
              fields={fieldsetDefinition.children}
              value={value}
              onEdit={() => {
                sidebarDispatch({
                  type: 'openEditCustomFields',
                  payload: [
                    {
                      value,
                      definition: fieldsetDefinition,
                    },
                  ],
                });
              }}
            />
          );
        })}

        {simpleCustomFields?.map((customField, i) => {
          const definition = CustomFieldDefinition.fromSchema(
            customField.field.schema
          );

          const value = customField.value
            ? JSON.parse(customField.value)
            : null;

          return (
            <CustomFieldsViewCard
              key={`${i}-${definition.identifier}`}
              label={customField.field.label}
              fields={[definition]}
              value={{
                [customField.field.identifier]: value,
              }}
              onEdit={() => {
                let payload: CustomFieldWithValue;

                if (definition.typecheckValue(value)) {
                  payload = {
                    value: value,
                    definition: definition,
                  };
                } else {
                  payload = {
                    value: null,
                    definition: definition,
                  };
                }

                sidebarDispatch({
                  type: 'openEditCustomFields',
                  payload: [payload],
                });
              }}
            />
          );
        })}
      </div>

      <EditCustomFieldsSidebar
        isOpen={sidebarState.activeSidebar === 'editCustomFields'}
        onClose={() => sidebarDispatch({ type: 'closeSidebar' })}
        fields={sidebarState.fields || []}
        initialValue={sidebarState.value}
        fieldsetIdentifier={sidebarState.identifier}
        fieldsetLabel={sidebarState.label}
        {...props}
      />
    </>
  );
};

const CustomFieldsViewCard: FC<{
  label: string;
  fields: CustomFieldHelperType[CustomFieldType]['definition'][];
  value: CustomFieldHelperType['fieldset']['value'] | null;
  onEdit?: () => void;
}> = (props) => {
  const { label, fields, value, onEdit } = props;
  const count = value ? Object.values(value).filter((x) => x).length : 0;
  const { data: users } = useGetMyAccountUsersIncludeDeactivated();

  return (
    <Card>
      <Card.Body>
        <Accordion
          title={
            <div className="flex gap-4 items-start w-full justify-between">
              <h4>
                {label}
                <Badge className="ml-2" variant="white" size="sm" rounded>
                  {count}
                </Badge>
              </h4>

              <Button
                className="-m-2"
                variant={'white'}
                size={'sm'}
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit?.();
                }}
              >
                Edit
              </Button>
            </div>
          }
          chevronPosition="left"
        >
          <ViewFieldset className="mt-2">
            <CustomFieldReadonlyGroup
              definitions={fields}
              value={value}
              users={users}
            />
          </ViewFieldset>
        </Accordion>
      </Card.Body>
    </Card>
  );
};
